import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import { FormattedDate, CurrencyValue } from '@jutro/components';

import {
    DataTable,
    DisplayColumn,
    defaultColumnFilter,
} from '@jutro/legacy/datatable';
import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames/bind';

import metadata from './MLPolicyBillingHistoryComponent.metadata.json5';
import messages from './MLPolicyBillingHistoryComponent.messages';
import styles from './MLPolicyBillingHistoryComponent.module.scss';

import { Button } from '@jutro/legacy/components';

const NUM_ROWS_TO_SHOW = 6;

const cx = classNames.bind(styles)

const MLPolicyBillingHistoryComponent = (props) => {
    const {
        billingSummary,
        formatAmountString
    } = props;
    const translator = useTranslator();
    const [activeCards, setActiveCards] = useState(['']);
    const [visibleBillingTransactions, setVisibleBillingTransactions] = useState(billingSummary.policyBillingHistory_ML.slice(0, NUM_ROWS_TO_SHOW))

    const showViewAll = () => {
        return billingSummary.policyBillingHistory_ML.length > NUM_ROWS_TO_SHOW && visibleBillingTransactions.length <= NUM_ROWS_TO_SHOW;
    };

    const showCollapse = () => {
        return billingSummary.policyBillingHistory_ML.length > NUM_ROWS_TO_SHOW && visibleBillingTransactions.length > NUM_ROWS_TO_SHOW;
    };

    const toggleNumRowsVisible = () => {
        setVisibleBillingTransactions(showViewAll() ? billingSummary.policyBillingHistory_ML : billingSummary.policyBillingHistory_ML.slice(0, NUM_ROWS_TO_SHOW));
    };

    const goToSelectedAccordion = (thisCard) => {
        if (_.find(activeCards, (c) => c === thisCard)) {
            setActiveCards(['']);
        } else {
            setActiveCards([thisCard]);
        }
    };

    const isCardActive = (thisCard) => {
        return _.find(activeCards, (c) => c === thisCard);
    };

    const renderHeader = (active, card) => {
        return (
            <div className="row">
                <div className="col-md-6">
                    <h2 className="section-header vertical-spacing-20">{translator(messages.billingHistory)}</h2>
                    <p>{translator(messages.transactionsWillNotDisplay)}</p>
                </div>
                <div className="col-md-6">
                    <Button
                        className="pull-right"
                        type="outlined"
                        onClick={() => goToSelectedAccordion(`${card}`)}
                    >
                        {isCardActive(`${card}`) ? translator(messages.collapse) : translator(messages.viewAll)}
                    </Button>
                </div>
            </div>
        )
    };

    const renderEffectiveDate = (item) => {
        return moment(item.effectiveDate).format('MM/DD/YYYY');
    };

    const renderTransactionDate = (item) => {
        return moment(item.transactionDate).format('MM/DD/YYYY');
    };

    const renderTransactionType = (item) => {
        return item.transactionType;
    };

    const renderTransactionAmt = (item) => {
        return formatAmountString(item.transactionAmount);
    };

    const renderRemainingBalance = (item) => {
        return formatAmountString(item.remainingBalance);
    };

    const getTransactionDateTime = (rowDate, rowTime, billingCenterTz) => {
        // Convert time from BC Java VM to User's Local Time
        const date = new Date(Date.UTC(rowDate.year, rowDate.month, rowDate.day, rowTime.hour, rowTime.minute, rowTime.second));
        const utcDate = new Date(date.toLocaleString('en-US', { timeZone: "UTC" }));
        const tzDate = new Date(date.toLocaleString('en-US', { timeZone: billingCenterTz }));
        const offset = utcDate.getTime() - tzDate.getTime();

        date.setTime(date.getTime() + offset);

        return date
    }

    const getTransactionAmount = (amount) => (amount?.amount === 0) ? null : amount

    const generateTableCellStyles = (hasGrid, isBold) => ({
        headerClassName: styles['table-header'],
        cellClassName: cx('cell-background', 'cell-size', { 'cell-grid-left': hasGrid, 'cell-bold': isBold })
    })

    const policyHistoryTable = (policyHistoryProps) => <DataTable {...policyHistoryProps}>
        <DisplayColumn
            key="effectiveDate"
            id="effectiveDate"
            textAlign="left"
            header="Effective Date"
            path="effectiveDate"
            {...generateTableCellStyles()}
            columnProportion={1}
            renderCell={(row) => <FormattedDate value={row.effectiveDate} displayFormat="vshort" />}
            sortable={false}
        />
        <DisplayColumn
            key="transactionDate"
            id="transactionDate"
            textAlign="left"
            header="Transaction Date/Time"
            path="transactionDateTime"
            {...generateTableCellStyles(true)}
            columnProportion={2}
            renderCell={(row) => <FormattedDate value={ getTransactionDateTime(row.transactionDate, row.transactionTime, row.transactionTime.timeZoneId_Ext)} displayFormat="vshort" inputFormat="MM/dd/yyy hh:mm" />}
            sortable={false}
        />
        <DisplayColumn
            key="transactionType"
            id="transactionType"
            textAlign="left"
            header="Transaction Type"
            path="transactionType"
            {...generateTableCellStyles(true, true)}
            columnProportion={4}
            sortable={false}
        />
        <DisplayColumn
            key="transactionAmount"
            id="transactionAmount"
            textAlign="left"
            header="Transaction Amount"
            path="transactionAmount"
            {...generateTableCellStyles(true)}
            columnProportion={2}
            renderCell={(row) => <CurrencyValue value={getTransactionAmount(row.transactionAmount)} className={styles['cell-size']} showFractions />}
            sortable={false}
        />
    </DataTable>

    const overrideProps = {
        accordionContainer: {
            accordionStates: activeCards
        },
        policyHistoryCard: {
            renderHeader: renderHeader
        },
        policyHistoryTable: {
            data: visibleBillingTransactions,
            showSearch: false,
            showPagination: false,
            columnsConfigurable: false,
            onColumnFilter: defaultColumnFilter,
        },
        viewAllBtnContainer: {
            visible: showViewAll()
        },
        collapseBtnContainer: {
            visible: showCollapse()
        }
    }

    const resolvers = {
        resolveCallbackMap: {
            renderHeader,
            renderEffectiveDate,
            renderTransactionDate,
            renderTransactionType,
            renderTransactionAmt,
            renderRemainingBalance,
            toggleNumRowsVisible
        },
        resolveClassNameMap: styles,
        resolveComponentMap: {
            policyHistoryTable,
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLPolicyBillingHistoryComponent.propTypes = {
    billingSummary: PropTypes.shape({
        policyBillingHistory_ML: PropTypes.array
    }),
    formatAmountString: PropTypes.func
};

MLPolicyBillingHistoryComponent.defaultProps = {
};

export default MLPolicyBillingHistoryComponent;